import React from 'react';
import GuideHero from 'components/guide-hero';
import {
  CitationBlock,
  CitationExampleBox,
  GuideHeader,
  GuideSection,
  GuideTitle,
  GuideWrapper,
  IntextCitation,
  TextWithColor,
} from 'components/guide';
import GuideLinkButton, {
  GuideLinkButtonSection,
} from 'components/guide-link-button';
import Footer from 'components/footer';
import Link from 'next/link';

const MlaGuide: React.FC = () => (
  <>
    <GuideHero
      header="Our Complete Guide to Citing in MLA 9 Format"
      imgSrc={require('images/MLA-guide-hero.png')}
      imgAlt="Laptop with screenshot on screen of manual for MLA format"
    >
      <p>
        In academia, citations help you avoid plagiarism and demonstrate your
        credibility as a researcher. The MLA format is widely used for this
        purpose, particularly for writing papers in literature and humanities.
      </p>
      <p>
        Our comprehensive MLA guide will walk you through the process of citing
        various types of sources in MLA, as well as formatting your Works Cited
        list. With plenty of specific examples and step-by-step instructions,
        you&apos;ll be able to master the art of MLA citation in no time.
      </p>
      <p>Get ready to master the art of MLA citations!</p>
    </GuideHero>
    <GuideWrapper>
      <GuideTitle>Discover More Bibcitation Guides</GuideTitle>
      <GuideLinkButtonSection>
        <GuideLinkButton
          text="Chicago Guide"
          href="/chicago"
          variant="purple"
        />
        <GuideLinkButton text="IEEE Guide" href="/ieee" variant="blue" />
        <GuideLinkButton text="APA Guide" href="/apa" variant="orange" />
      </GuideLinkButtonSection>

      <FormatOverview />
      <InTextCitations />
      <WorksCited />
      <BookCitations />
      <JournalCitations />
      <WebsiteCitations />
      <BookChapterCitations />
      <EBookCitations />
      <MagazineArticleCitations />
      <NewspaperArticleCitations />
      <ConferencePaperCitations />
      <AudioVisualCitations />
      <PodcastCitations />
      <SocialMediaCitations />
      <ImageArtworkCitations />
      <ThesisDissertationCitations />
      <DatabaseDataSetCitations />
      <MobileAppCitations />
      <ClassicalWorksCitations />
      <LegalDocumentsCitations />
      <OnlineLecturesCitations />
      <CourseMaterialsCitations />
      <PersonalCommunicationsCitations />
      <FinalNotes />
    </GuideWrapper>
    <Footer />
  </>
);

const FormatOverview: React.FC = () => (
  <GuideSection id="Overview">
    <GuideHeader>MLA Format Overview</GuideHeader>
    <p>
      <strong>The Modern Language Association (MLA)</strong> citation format is
      a widely used citation style for academic papers in the humanities and
      liberal arts. Designed to provide a clear and consistent method of citing
      sources, the MLA format helps writers avoid plagiarism and give proper
      credit to their sources. This citation style uses two main components:
      in-text citations and the Works Cited page.{' '}
    </p>
    <p>
      <strong>In-text citations</strong> appear within the body of the paper,
      providing a brief reference to the source, usually including the
      author&apos;s last name and the page number where the information was
      found. In-text citations correspond to the full citation found on the
      Works Cited page, which is organized alphabetically by the author&apos;s
      last name.
    </p>
    <p>
      <strong>The Works Cited page</strong> in the MLA citation format provides
      a comprehensive list of all sources used in the paper, offering detailed
      information about each source to help readers locate them. The format
      varies slightly based on the type of source, such as a book, journal
      article, or website. The MLA citation style prioritizes clarity and
      simplicity, making it easy for both writers and readers to understand and
      navigate the sources used in a paper.
    </p>
  </GuideSection>
);

const InTextCitations: React.FC = () => (
  <GuideSection id="InText">
    <GuideHeader>Insert MLA In-text Citations</GuideHeader>
    <p>
      In the MLA citation format, in-text citations are brief references within
      the body of a paper that guide readers to the full citation in the Works
      Cited page. They typically include the author&apos;s last name and the
      page number where the information was found. Here you can find detailed
      examples of in-text citations in the MLA format:
    </p>
    <p>
      <strong>Basic in-text citation:</strong> The most common in-text citation
      includes the author&apos;s last name and the page number(s) in
      parentheses, placed at the end of the sentence before the period. For
      example: <IntextCitation>(Smith 42)</IntextCitation>.
    </p>

    <p>
      <strong>Multiple authors:</strong> If a source has two authors, include
      both last names in the citation, separated by &quot;and.&quot; For
      example: <IntextCitation>(Smith and Johnson 58)</IntextCitation>. If a
      source has three or more authors, include the first author&apos;s last
      name followed by &quot;et al.&quot; For example:{' '}
      <IntextCitation>(Smith et al. 22)</IntextCitation>.
    </p>

    <p>
      <strong>Multiple works by the same author:</strong> If citing multiple
      works by the same author, include a shortened version of the title to
      differentiate between the sources. For example:{' '}
      <IntextCitation>
        (Smith, &quot;A Journey into the Universe&quot; 12)
      </IntextCitation>{' '}
      and{' '}
      <IntextCitation>
        (Queen, <em>Rose Water</em> 35)
      </IntextCitation>
      .
    </p>

    <p>
      <strong>No author:</strong> If the source has no author, use a shortened
      version of the title in quotation marks for articles or in italics for
      longer works, such as books or websites. For example:{' '}
      <IntextCitation>(&quot;Article Title&quot; 5)</IntextCitation> or{' '}
      <IntextCitation>
        (<em>Book Title</em> 32)
      </IntextCitation>
      .
    </p>

    <p>
      <strong>Citing multiple sources in one sentence:</strong> If referencing
      multiple sources in one sentence, separate each citation with a semicolon.
      For example: <IntextCitation>(Smith 42; Bart 78)</IntextCitation>.
    </p>

    <p>
      <strong>Indirect citations (quotations within a source):</strong> If
      citing a quotation from a source that is itself quoting another source,
      use the abbreviation &quot;qtd. in&quot; before the indirect source. For
      example: <IntextCitation>(qtd. in Smith 15)</IntextCitation>.
    </p>

    <p>
      <strong>Electronic sources without page numbers:</strong> When citing
      electronic sources without page numbers, use the author&apos;s last name
      only or a shortened version of the title if no author is available. For
      example: <IntextCitation>(Smith)</IntextCitation> or{' '}
      <IntextCitation>(&quot;Article Title&quot;)</IntextCitation>.
    </p>

    <p>
      <strong>Audiovisual sources:</strong> If citing a film, television show,
      or other audiovisual source, use the title (italicized) and a timestamp
      (hours, minutes, and seconds) instead of a page number. For example:{' '}
      <IntextCitation>
        (<em>Citizen Kane</em> 01:22:15)
      </IntextCitation>
      .
    </p>

    <p>
      <strong>For long quotations,</strong> use a block quote format. Indent the
      entire quotation one inch from the left margin and do not use quotation
      marks. Place the in-text citation after the closing punctuation.
    </p>

    <p>
      <strong>Example:</strong>
    </p>

    <p>
      <IntextCitation>Smith</IntextCitation> emphasizes the importance of proper
      citation:
    </p>

    <p>
      Citing sources correctly is essential for maintaining academic integrity
      and avoiding plagiarism. By giving credit to the original authors, you
      demonstrate respect for their work and allow readers to locate the sources
      you used. <IntextCitation>(25)</IntextCitation>
    </p>
  </GuideSection>
);

const WorksCited: React.FC = () => (
  <GuideSection id="WorksCited">
    <GuideHeader>Format Your MLA Works Cited</GuideHeader>
    <p>
      A Works Cited page is an essential part of any research paper or academic
      work using the MLA citation format. It provides a comprehensive list of
      all sources cited in the paper, allowing readers to locate and verify the
      sources used.
    </p>
    <p>
      Here are detailed instructions on how to format a Works Cited page in MLA
      format:
    </p>

    <ul>
      <li>
        <strong>Choose placement and title:</strong> The Works Cited page should
        begin on a separate page at the end of your paper. Center the title
        &quot;Works Cited&quot; (without quotation marks) at the top of the
        page. Do not bold, italicize, or underline the title.
      </li>
      <li>
        <strong>Use correct spacing and margins:</strong> Use double-spacing
        throughout the Works Cited page. Maintain 1-inch margins on all sides of
        the page. The first line of each citation should be flush with the left
        margin. Indent any additional lines by 0.5 inches (a hanging indent).
      </li>
      <li>
        <strong>Organize citations:</strong> Sort the list of sources in
        alphabetical order by the author&apos;s last name. If a source does not
        have an author, use the title to alphabetize the entry. Ignore articles
        (&quot;a,&quot; &quot;an,&quot; &quot;the&quot;) when alphabetizing by
        title.
      </li>
      <li>
        <strong>Format entries correctly:</strong> Follow the general format for
        each source type (e.g., books, articles, websites) as specified in the
        MLA Handbook. Include the author&apos;s name, the title of the work, the
        container (if applicable), and other relevant information such as the
        publisher, publication date, and location.
      </li>
      <li>
        <strong>Don&apos;t forget about punctuation and capitalization:</strong>{' '}
        Use title case for titles, capitalizing the first word, the last word,
        and all other major words. Use punctuation marks consistently, following
        the guidelines in the MLA Handbook.
      </li>
      <li>
        <strong>Check for accuracy:</strong> Be sure to check each reference for
        accuracy, including spelling and punctuation, as well as proper
        capitalization and italics.
      </li>
    </ul>
    <img
      src={require('images/MLA-screenshot.png')}
      alt="Screenshot of an MLA reference page"
    />
    <p>
      Remember to consult the MLA Handbook for specific guidelines and examples
      for various source types. As you add sources to your Works Cited page, be
      sure to double-check the formatting to ensure consistency and accuracy.
    </p>
  </GuideSection>
);

const BookCitations: React.FC = () => (
  <GuideSection id="Book">
    <GuideHeader>Cite Books in MLA</GuideHeader>
    <p>To cite a book in your Works Cited list, follow this format: </p>
    <CitationBlock header="Citing a book in MLA">
      Author&apos;s Last Name, First Name. <em>Title of the Book.</em> Edition,
      Publisher, Publication Year.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Rowling, J.K. <em>Harry Potter and the Philosopher&apos;s Stone.</em>{' '}
      Bloomsbury, 1997.
    </CitationExampleBox>
    <p>
      If the book is an edited volume, use the word &quot;editor&quot; or
      &quot;editors&quot; after the editor&apos;s name, depending on whether it
      is a single or multiple editors.
    </p>
    <p>
      <strong>Citing an edited book:</strong>
    </p>
    <CitationExampleBox>
      McQueen, Alexander, <TextWithColor>editor</TextWithColor>.{' '}
      <em>Perspectives on Modern Fashion.</em> London Press, 2001.
    </CitationExampleBox>
    <p>
      For books with multiple editions, specify the edition after the title,
      using ordinal numbers (e.g., &quot;2nd ed.&quot;).
    </p>

    <p>
      <strong>Citing a book with multiple editions:</strong>
    </p>
    <CitationExampleBox>
      Jobs, Steve. <em>How to Build a Powerful Brand.</em>{' '}
      <TextWithColor>3nd ed.</TextWithColor> Routledge, 2005.
    </CitationExampleBox>

    <p>
      If a book is part of a series or multivolume set, include the volume
      number after the title, preceded by &quot;vol.&quot; In cases where a book
      has no author, the citation should begin with the title.
    </p>

    <p>
      When citing a book that has been translated, include the translator&apos;s
      name after the title, preceded by the word &quot;Translated by.&quot;
    </p>
    <p>
      <strong>Citing a translated book:</strong>
    </p>
    <CitationExampleBox>
      Camus, Albert. <em>The Stranger. </em>
      <TextWithColor>Translated by Matthew Ward</TextWithColor>, Vintage
      International, 1989.
    </CitationExampleBox>
    <p>
      When citing a specific chapter or essay within an edited book or
      anthology, start with the author&apos;s name, followed by the title of the
      chapter or essay in quotation marks, then the title of the book, the
      editor&apos;s name, and the page numbers of the cited section.
    </p>
    <p>
      <strong>Citing a specific portion of a book:</strong>
    </p>
    <CitationExampleBox>
      Morrison, Toni. &quot;Memory, Creation, and Writing.&quot;{' '}
      <em>Inventing the Truth: The Art of Memoir</em>,{' '}
      <TextWithColor>
        edited by William Zinsser, Hugh Mifflin, 1995, pp. 103-124.
      </TextWithColor>
    </CitationExampleBox>
    <p>
      These examples cover various scenarios and exceptions for citing books in
      MLA format. Remember to consult the MLA Handbook for more information and
      specific guidelines when formatting your citations.
    </p>
  </GuideSection>
);

const JournalCitations: React.FC = () => (
  <GuideSection id="Journal">
    <GuideHeader>Cite Journal Articles in MLA</GuideHeader>
    <p>When citing a journal article in MLA format, follow this template:</p>
    <CitationBlock header="Citing a journal article in MLA">
      Author&apos;s Last Name, First Name. &quot;Article Title.&quot;{' '}
      <em>Journal Name</em>, vol. Volume Number, no. Issue Number, Publication
      Date, pp. page numbers, DOI (if available).
    </CitationBlock>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Swan, Bella. &quot;The Impact of Vampire Relationship on Mental
      Health.&quot; <em>The Twilight Review</em>, vol. 11, no. 1, 2007, pp.
      123-140.
    </CitationExampleBox>
    <p>
      For the journal articles that you accessed online, from JSTOR or a
      different online database, make sure to include the DOI or the URL if the
      DOI is unavailable.
    </p>
    <p>
      <strong>Citing a journal article accessed online:</strong>
    </p>
    <CitationExampleBox>
      Brown, James. &quot;The Future of Work in the Digital Age.&quot;{' '}
      <em>Future Studies Quarterly,</em> vol. 8, no. 2, 2019, pp. 72-89,{' '}
      <TextWithColor>doi:10.1093/fsq/8.2.72.</TextWithColor>
    </CitationExampleBox>
    <p>
      If an article has two authors, include both authors&apos; names separated
      by &quot;and.&quot;
    </p>
    <p>
      <strong>Citing an article with two authors:</strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>Smith, John, and Emily Johnson.</TextWithColor> &quot;The
      Effects of Climate Change on Agriculture.&quot;{' '}
      <em>Environmental Science Journal,</em> vol. 12, no. 4, 2019, pp. 45-62.
    </CitationExampleBox>

    <p>
      If an article has three or more authors, list only the first author,
      followed by &quot;et al.&quot;
    </p>
    <p>
      <strong>Citing an article with three authors:</strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>Adams, Paul, et al.</TextWithColor> &quot;Exploring the
      Role of Artificial Intelligence in Education.&quot;{' '}
      <em>Journal of Educational Technology,</em> vol. 17, no. 1, 2021, pp.
      15-28.
    </CitationExampleBox>

    <p>
      In some cases, the journal article may not have an official author or some
      other elements, like the volume or issue number. In this situation, the
      title of the article is used in place of the author&apos;s name, while the
      volume and issue are omitted.
    </p>
    <p>
      <strong>
        Citing an article without an author, volume or issue number:
      </strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>
        &quot;The Power of Mindfulness in Stress Reduction.&quot;
      </TextWithColor>{' '}
      <em>Mindfulness Today,</em> 2020,
      www.mindfulnesstoday.com/power-of-mindfulness.
    </CitationExampleBox>
  </GuideSection>
);

const WebsiteCitations: React.FC = () => (
  <GuideSection id="Website">
    <GuideHeader>Cite Websites in MLA</GuideHeader>
    <p>
      Citing a webpage on your MLA Works Cited page? Use the following format:
    </p>
    <CitationBlock header="Citing a Website in MLA">
      Author&apos;s Last Name, First Name (if available). &quot;Web Page
      Title.&quot; <em>Website Title,</em> Publication Date, URL.
    </CitationBlock>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Johnson, Emily. &quot;Mastering MLA Citations.&quot; <em>Bibcitation,</em>{' '}
      29 Apr. 2023, www.bibcitation.com/blog/mastering-mla-citations.
    </CitationExampleBox>
    <p>
      However, web pages may often not have an identified author. In this
      situation, the title of the article is used in place of the author&apos;s
      name. For example:
    </p>
    <p>
      <strong>Citing a web page without an author:</strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>
        &quot;The Benefits of Exercise for Mental Health.&quot;
      </TextWithColor>{' '}
      <em>Health and Wellness,</em> 15 Feb. 2019,
      www.healthandwellness.com/benefits-of-exercise.
    </CitationExampleBox>
    <p>
      When citing a website with an organization as the author, you can use the
      organization&apos;s name as both the author and the website name if they
      are the same.
    </p>
    <p>
      <strong>Citing a website with an organization as the author:</strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>World Health Organization.</TextWithColor> &quot;Global
      Health Risks and Challenges.&quot; <em>WHO,</em> 12 Jan. 2020,
      www.who.int/global-health-risks.
    </CitationExampleBox>
    <p>
      <strong>
        If no publication date is available, use the abbreviation
        &quot;n.d.&quot; (no date).
      </strong>
    </p>

    <p>
      Moreover, for online sources without a publication date or for sources
      that can be updated or changed without notice (like web pages), it is
      recommended to include the &quot;Accessed&quot; date. This provides a
      clearer timeline of when the researcher viewed the material and ensures
      accuracy, as the content might have been updated or removed since that
      date. Using the &quot;Accessed&quot; date is especially useful for
      materials that are subject to change or when a source&apos;s stability
      cannot be guaranteed.
    </p>
    <p>
      <strong>Example:</strong>
    </p>

    <CitationExampleBox>
      Epstein, Sophia. &quot;Why AI won&apos;t be the burnout cure we&apos;ve
      been waiting for.&quot; <em>BBC,</em> 6 June 2023,
      https://www.bbc.com/worklife/article/20230605-why-ai-wont-be-the-burnout-cure-weve-been-waiting-for.{' '}
      <TextWithColor>Accessed 8 June 2023.</TextWithColor>
    </CitationExampleBox>
  </GuideSection>
);

const BookChapterCitations: React.FC = () => (
  <GuideSection id="BookChapter">
    <GuideHeader>Cite Book Chapters in MLA</GuideHeader>
    <p>
      Citing a book chapter in MLA format requires you to include the chapter
      author and the book editor(s). The format is:
    </p>
    <CitationBlock header="Citing a book chapter in MLA">
      Author&apos;s Last Name, First Name. &quot;Chapter Title.&quot;{' '}
      <em>Book Title,</em> edited by Editor Name, Publisher Name, Publication
      Date, pp. page numbers.
    </CitationBlock>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Miller, Jane. &quot;The Impact of Climate Change on Polar Bears.&quot;{' '}
      <em>Wildlife Conservation: Challenges and Solutions,</em> edited by John
      Gray, Nature Press, 2015, pp. 56-78.
    </CitationExampleBox>
    <p>
      If the book is a translation, include the translator&apos;s name after the
      editor&apos;s name (if any), preceded by the phrase &quot;translated
      by.&quot;
    </p>
    <p>
      <strong>Citing a chapter in a translated book:</strong>
    </p>
    <CitationExampleBox>
      García Márquez, Gabriel. &quot;Chapter 5.&quot;{' '}
      <em>One Hundred Years of Solitude,</em> edited by John Simmons,{' '}
      <TextWithColor>translated by Gregory Rabassa</TextWithColor>, Harper &
      Row, 1970, pp. 82-96.
    </CitationExampleBox>
    <p>
      If the chapter has a unique title within a larger work, such as a play
      within an anthology or a collection of works by different authors, include
      the title of the larger work in italics, followed by the editor&apos;s
      name (if any) and the page numbers of the cited section.
    </p>
    <p>
      <strong>Citing a chapter from a collection of works:</strong>
    </p>
    <CitationExampleBox>
      Shakespeare, William.{' '}
      <TextWithColor>
        &quot;Romeo and Juliet.&quot;{' '}
        <em>The Complete Works of William Shakespeare</em>
      </TextWithColor>
      , edited by David Bevington, Bantam, 2005, pp. 1023-1076.
    </CitationExampleBox>
    <p>
      If the book is a multivolume set, include the volume number after the book
      title and edition (if available), preceded by &quot;vol.&quot;
    </p>
    <p>
      For chapters with supplementary materials or appendices, include the
      relevant page numbers or identifiers in the citation.
    </p>
  </GuideSection>
);

const EBookCitations: React.FC = () => (
  <GuideSection id="Ebook">
    <GuideHeader>Cite Ebooks in MLA</GuideHeader>
    <p>
      Citing an ebook in MLA format follows similar guidelines to citing a
      printed book, but with a few additional details. The basic format for
      citing an ebook is as follows:
    </p>
    <CitationBlock header="Citing an ebook in MLA">
      Author&apos;s Last Name, First Name. <em>Title of the Ebook.</em> Edition
      (if any), Publisher, Publication Year.{' '}
      <em>Website or Library Database Name,</em> URL or DOI.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Melville, Herman <em>Moby-Dick; or, The Whale.</em> Harper & Brothers,
      1851. <em>Project Gutenberg,</em>{' '}
      https://www.gutenberg.org/files/2701/2701-h/2701-h.htm.
    </CitationExampleBox>
    <p>
      If the ebook has a DOI (Digital Object Identifier), the citation should
      include it at the end of the reference.
    </p>
    <p>
      <strong>Citing an ebook with a DOI:</strong>
    </p>
    <CitationExampleBox>
      Brown, James. <em>The Age of Artificial Intelligence.</em> New York Press,
      2023, <TextWithColor>https://doi:10.1093/ai/12345.</TextWithColor>
    </CitationExampleBox>
    <p>
      If your ebook was only available on a tablet, e-reader, or a reading app
      such as Kindle, all you need to do is to include the words &quot;E-book
      ed.&quot;after the title.
    </p>
    <p>
      <strong>Citing an ebook from a personal device:</strong>
    </p>
    <CitationExampleBox>
      DiCaprio, Leonardo. <em>Surviving Titanic.</em>{' '}
      <TextWithColor>E-book ed.</TextWithColor>, Hollywood Classics, 2004.
    </CitationExampleBox>
    <p>
      In some scenarios, additional elements need to be included in the
      citation. If the ebook is part of a series, provide the series name and
      volume number after the publisher and before the publication date.
    </p>
    <p>
      If the ebook is a translation, include the translator&apos;s name after
      the editor&apos;s name (if available), preceded by the phrase
      &quot;translated by.&quot; For ebooks with supplementary materials,
      include the relevant page numbers or identifiers in the citation.
    </p>
  </GuideSection>
);

const MagazineArticleCitations: React.FC = () => (
  <GuideSection id="MagazineArticle">
    <GuideHeader>Cite Magazine Articles in MLA</GuideHeader>
    <p>
      To cite a magazine article in your MLA Works Cited page, use the following
      format:
    </p>
    <CitationBlock header="Citing a magazine article in MLA">
      Author&apos;s Last Name, First Name. &quot;Article Title.&quot;{' '}
      <em>Magazine Title,</em> Publication Date, pp. page numbers (and URL or
      DOI for online articles).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Roberts, Julia. &quot;The Lessons I Learned from Eat, Pray, Love.&quot;
      <em> Vogue,</em> May 2021, pp. 45-49.
    </CitationExampleBox>
    <p>
      If the magazine article is found online, you should also provide the URL
      or DOI at the end of the citation.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Martinez, Carlos. &quot;The Rise of Electric Vehicles in the Automotive
      Industry.&quot; <em>Auto World,</em> 10 June 2022,{' '}
      <TextWithColor>
        https://autoworld.com/rise-of-electric-vehicles.
      </TextWithColor>
    </CitationExampleBox>
    <p>
      If the magazine is published seasonally (e.g., Winter, Spring, Summer, or
      Fall) rather than monthly, include the season and year as the publication
      date (e.g., Winter 2021).
    </p>
  </GuideSection>
);

const NewspaperArticleCitations: React.FC = () => (
  <GuideSection id="NewspaperArticle">
    <GuideHeader>Cite Newspaper Articles in MLA</GuideHeader>
    <p>
      For newspaper articles, the MLA citation format is similar to magazine
      articles:
    </p>
    <CitationBlock header="Citing a newspaper article in MLA">
      Author&apos;s Last Name, First Name. &quot;Article Title.&quot; Newspaper
      Title, Edition or Section (if available), Publication Date, pp. page
      numbers (and URL or DOI for online articles).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Seyfried, Amanda. &quot;How &apos;Mamma Mia!&apos; Changed My
      Acting.&quot; <em>The Daily Tribune,</em> 12 May 2021, pp. A12-A14.
    </CitationExampleBox>
    <p>
      If the article is found online, include the URL or the DOI (Digital Object
      Identifier) if available, but do not include the page numbers.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Thompson, Emily. &quot;S&P 500 vs Bitcoin: Who Will Win?&quot;{' '}
      <em>The Wall Street Journal,</em> 23 May 2023,{' '}
      <TextWithColor>https://wsj.com/sp500-bitcoin-case-study.</TextWithColor>
    </CitationExampleBox>
    <p>
      If the newspaper isn&apos;t widely recognized or is a local paper, include
      the name of the city in parentheses following the newspaper&apos;s title.
    </p>
    <p>
      If the article is available in both print and digital formats, the
      citation should be identical for both, but for digital articles, you
      should also include a DOI or a URL at the end of the citation.
    </p>
  </GuideSection>
);

const ConferencePaperCitations: React.FC = () => (
  <GuideSection id="ConferencePaper">
    <GuideHeader>Cite Conference Papers in MLA</GuideHeader>
    <p>
      To cite published conference proceedings in MLA, use the following format:
    </p>
    <CitationBlock header="Citing a conference paper in MLA">
      Author&apos;s Last Name, First Name. &quot;Paper Title.&quot;{' '}
      <em>Proceedings Title, Conference Date, Location.</em> Publisher,
      Publication Date, pp. page numbers or DOI (if available).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Johns, Sarah. &quot;The Role of Education in African Development.&quot;{' '}
      <em>
        Proceedings of the Duke University Annual Conference on International
        Development, 12-14 Aug. 2019, Durham, NC.
      </em>{' '}
      Duke University Press, 2020, pp. 212-216.
    </CitationExampleBox>
    <p>
      After the author&apos;s name, make sure to include the title of the paper
      in quotation marks, followed by the title of the conference (italicized),
      the date and location of the conference, and any relevant publication
      information (for instance, whether the paper has been published).
    </p>

    <p>
      When a conference paper is part of a published conference proceedings,
      include the editor&apos;s name (if available) preceded by &quot;edited
      by,&quot; the title of the proceedings (italicized), the publisher, and
      the publication date.
    </p>
    <p>
      For online conference papers, provide the URL or DOI (if available) at the
      end of the citation.
    </p>
    <p>
      <strong>Citing an edited conference paper available online:</strong>
    </p>
    <CitationExampleBox>
      Thompson, Emily. &quot;The Role of Social Media in Political
      Campaigns.&quot;{' '}
      <em>
        Proceedings of the Digital Democracy Summit, 10-12 Feb. 2021,
        Washington, D.C.
      </em>{' '}
      <TextWithColor>Edited by Michael Williams</TextWithColor>, Washington
      State Press, 2017, pp. 10-17.{' '}
      <TextWithColor>
        <em>George Washington Online Library,</em>{' '}
        https://doi:10.1234/ddc.2021.0001.
      </TextWithColor>
    </CitationExampleBox>
    <p>
      Remember that each specific conference paper may require additional
      information or variations in the citation format.
    </p>
  </GuideSection>
);

const AudioVisualCitations: React.FC = () => (
  <GuideSection id="AudioVisual">
    <GuideHeader>Cite Audio &amp; Visual Media in MLA</GuideHeader>
    <p>
      For audiovisual materials like films, television series, and YouTube
      videos, include the contributor(s) most relevant to your source, such as
      the director, or if not available, then producer, or writer. The MLA
      citation format for films materials is:
    </p>
    <CitationBlock header="Citing a film in MLA">
      <em>Film Title.</em> Directed by First Name Last Name, performances by
      First Name Last Name (if relevant), Production Company, Year.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      <em>Inception.</em> Directed by Christopher Nolan, performance by Leonardo
      DiCaprio, Warner Bros. Pictures, 2010.
    </CitationExampleBox>
    <p>
      However, there are some exceptions to this format. When citing a TV-show
      episode, make sure to include the show title, as well as the season and
      episode numbers.
    </p>
    <p>
      <strong>Citing a TV-show episode:</strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>&quot;Trip to Stabby Town.&quot;</TextWithColor>
      <em> Lucifer,</em> created by Tom Kapinos, performance by Tom Ellis,{' '}
      <TextWithColor>season 2, episode 8</TextWithColor>, Fox, 2016.
    </CitationExampleBox>
    <p>
      If you&rsquo;re looking to cite a song, your citation should include the
      song title, as well as the name of the album and format (if applicable),
      or URL or DOI (if available).
    </p>
    <p>
      <strong>Citing a song:</strong>
    </p>
    <CitationExampleBox>
      Beyoncé.{' '}
      <TextWithColor>
        &quot;Formation.&quot; <em>Lemonade</em>
      </TextWithColor>
      , Parkwood Entertainment, 2016,
      www.beyonce.com/album/lemonade-visual-album/.
    </CitationExampleBox>
    <p>
      Remember that different scenarios and exceptions may require you to adapt
      these structures. Always consult the MLA Handbook for more information and
      specific guidelines when formatting your citations for audio and visual
      materials.
    </p>
  </GuideSection>
);

const PodcastCitations: React.FC = () => (
  <GuideSection id="Podcast">
    <GuideHeader>Cite Podcasts in MLA</GuideHeader>
    <p>
      To cite a podcast episode in MLA format, use the following citation
      structure:
    </p>
    <CitationBlock header="Citing a podcast in MLA">
      Last Name, First Name, host. &quot;Episode Title.&quot;{' '}
      <em>Podcast Title,</em> season number, episode number, Production Company
      or Publisher, Release Date, URL (if available).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Gladwell, Malcolm, host. &quot;McDonald&rsquo;s Broke My Heart.&quot;{' '}
      <em>Revisionist History,</em> season 2, episode 9, Pushkin, 15 June 2016,
      https://https://pushkin.fm/podcasts/revisionist-history/mcdonalds-broke-my-heart.
    </CitationExampleBox>

    <p>
      If the podcast episode does not have an episode number, omit it from the
      citation.
    </p>
    <p>
      If you need to cite a specific guest on the podcast, include their name
      and the description &quot;guest&quot; after the title of the podcast.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Vox, Ezra, host. &quot;Is Big Tech Really a Monopoly?&quot;{' '}
      <em>The Ezra Klein Show,</em>{' '}
      <TextWithColor>featuring Tim Wu</TextWithColor>, guest, Vox Media, 20 Feb.
      2019, https://vox.com/ezra-klein.
    </CitationExampleBox>
    <p>
      Keep in mind that you may need to adapt your citation based on the
      specific information available for the podcast and the focus of your
      discussion.
    </p>
  </GuideSection>
);

const SocialMediaCitations: React.FC = () => (
  <GuideSection id="SocialMedia">
    <GuideHeader>Cite Social Media Posts in MLA</GuideHeader>
    <p>
      For social media posts, use the following format in your MLA Works Cited:
    </p>
    <CitationBlock header="Citing a social media post in MLA">
      Author&apos;s Last Name, First Name (Username). &quot;Full Text or
      Description of the Post.&quot; <em>Platform,</em> Post Date, Time, URL.
    </CitationBlock>
    <p>
      <strong>Citing an Instagram post:</strong>
    </p>
    <CitationExampleBox>
      Hamilton Videos [@hamilton.vods]. Clip of King George singing in Hamilton.{' '}
      <em>Instagram,</em> 5 May 2018, instagram.com/p/CCPEUJLDz0l/.
    </CitationExampleBox>

    <p>
      If the account name and its corresponding handle are similar (e.g.,
      @aliciakeys and Alicia Keys), it is generally recommended to exclude the
      handle when including a URL in the entry. However, if you are not
      providing a URL, such as when referencing a mobile version of the site, it
      is advisable to include the handle, as it can assist your reader in
      locating the specific post.
    </p>

    <p>
      <strong>Citing a Twitter post:</strong>
    </p>
    <CitationExampleBox>
      Obama, Barack [@BarackObama]. &quot;There&apos;s no place like
      Chicago!&quot; <em>Twitter,</em> 12 Apr. 2023.
    </CitationExampleBox>

    <p>
      <strong>Citing a Facebook post:</strong>
    </p>
    <CitationExampleBox>
      World Wildlife Fund. &quot;Happy Earth Day from all of us at WWF!&quot;{' '}
      <em>Facebook,</em> 22 Apr. 2019,
      facebook.com/worldwildlifefund/photos/a.58993914793/10156574728914794.
    </CitationExampleBox>

    <p>
      Moreover, if you are accessing a post on the mobile version of a social
      media site and the copyright date is not visible, it is recommended to
      include the date you accessed the post as an additional element at the end
      of the entry.
    </p>
  </GuideSection>
);

const ImageArtworkCitations: React.FC = () => (
  <GuideSection id="ImageArtwork">
    <GuideHeader>Cite Images &amp; Artworks in MLA</GuideHeader>
    <p>
      To cite an online image or artwork in your MLA citation list, the format
      is:
    </p>
    <CitationBlock header="Citing an artwork in MLA">
      Last Name, First Name. <em>Title of Artwork.</em> Creation Year,
      Institution or Collection, City.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Van Gogh, Vincent. <em>The Starry Night.</em> 1889, Museum of Modern Art,
      New York.
    </CitationExampleBox>
    <p>
      For digital images or artwork viewed online, begin with the artist&apos;s
      name, the title of the artwork in italics, the date of creation (if
      known), the website hosting the artwork or the name of the database, and
      the URL or DOI (if available).
    </p>
    <p>
      <strong>Citing an image or artwork viewed online:</strong>
    </p>
    <CitationExampleBox>
      Hockney, David. &quot;A Bigger Splash.&quot; <em>Tate Gallery,</em> 1967,{' '}
      <TextWithColor>
        www.tate.org.uk/art/artworks/hockney-a-bigger-splash-t03254.
      </TextWithColor>
    </CitationExampleBox>
    <p>
      In cases where the creation date or author name is unknown, you can make
      adjustments to the citation format to account for the missing information.
    </p>
    <p>
      If the creation date of the artwork is unknown, you can use the
      abbreviation &quot;n.d.&quot; (no date) in place of the date.
    </p>
    <p>
      If the author of the artwork is unknown, you can start the citation with
      the title of the artwork in italics. If it&apos;s necessary to provide
      context or specify that the author is unknown, you can use the term
      &ldquo;Unknown Artist&rdquo;, &ldquo;Anonymous&rdquo; or
      &ldquo;Anon.&rdquo;.
    </p>
    <p>Citing an artwork with an unknown author and creation date:</p>
    <CitationExampleBox>
      <TextWithColor>Unknown Artist.</TextWithColor> <em> Blue Fresco.</em>{' '}
      <TextWithColor>n.d.</TextWithColor>, Catacombs of Rome, Rome.
    </CitationExampleBox>
    <p>
      Remember that citation requirements may vary depending on the context and
      the focus of your work. Always consult the MLA Handbook for more
      information and specific guidelines when formatting your citations with
      unknown information.
    </p>
  </GuideSection>
);

const ThesisDissertationCitations: React.FC = () => (
  <GuideSection id="ThesisDissertation">
    <GuideHeader>Cite Theses &amp; Dissertations in MLA</GuideHeader>
    <p>
      When citing a thesis or dissertation in your MLA paper, use the following
      format:
    </p>
    <CitationBlock header="Citing a thesis in MLA">
      Last Name, First Name. <em>Thesis Title.</em> Year. Institution Name,
      Description (PhD dissertation or MA thesis).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith, John.{' '}
      <em>An Examination of Urban Planning in Post-Industrial Cities.</em>
      2019. University of Chicago, PhD dissertation.
    </CitationExampleBox>
    <p>
      For a digital thesis or dissertation, begin with the author&apos;s name,
      the title of the work in italics, the year of completion, the institution
      where the work was completed, the description of the work as a thesis or
      dissertation, and the URL or DOI (if available).
    </p>
    <p>
      <strong>Citing a thesis or dissertation published online:</strong>
    </p>
    <CitationExampleBox>
      Shaw, Angela.{' '}
      <em>Digital Nomads As Agents of Post-Modern Colonialism?</em> 2025.
      University of Copenhagen, MA thesis.{' '}
      <TextWithColor>
        <em>UCPH,</em> research.ku.dk/thesis/863.
      </TextWithColor>
    </CitationExampleBox>
  </GuideSection>
);

const DatabaseDataSetCitations: React.FC = () => (
  <GuideSection id="DatabaseDataset">
    <GuideHeader>Cite Databases &amp; Data Sets in MLA</GuideHeader>
    <p>
      When citing datasets or statistical data, include the author, publication
      year, title of the dataset, version, publisher, and the DOI or URL in your
      MLA Works Cited page:
    </p>
    <CitationBlock header="Citing a database or data set in MLA">
      Author&apos;s Last Name, First Name (if available). &quot;Title of
      Document or Data Set.&quot; <em>Database Title,</em> Publication Date, URL
      or DOI.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      United States Census Bureau. &quot;American Community Survey (ACS).&quot;{' '}
      <em>Census Bureau,</em> 2018, www.census.gov/programs-surveys/acs/.
    </CitationExampleBox>
    <p>
      If you are citing a specific work within a database or using data from a
      data set, you should first cite the work or the data following the
      appropriate citation format for that type of source (e.g., a journal
      article, a book, a newspaper article), and then add the database or data
      set as a supplementary source at the end of the citation.
    </p>
  </GuideSection>
);

const MobileAppCitations: React.FC = () => (
  <GuideSection id="MobileApp">
    <GuideHeader>Cite Mobile Applications in MLA</GuideHeader>
    <p>
      Citing mobile applications in MLA format requires the author, the
      app&apos;s release year, the app&apos;s title, the version, and the
      platform in your reference list entry:
    </p>
    <CitationBlock header="Citing a mobile app in MLA">
      Last Name, First Name (if known). <em>Application Title.</em> Publisher or
      App Store, vers. Version, Release Date, Mobile App, URL.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      <em>Bibcitation for Microsoft Word.</em> Checker Software, vers. 21.34.0,
      2021, Mobile App, bibcitation.com/bibcitation-word-add-on.
    </CitationExampleBox>
    <p>
      Keep in mind that you may need to modify these examples based on the
      specific information available for the mobile application and the focus of
      your discussion.
    </p>
  </GuideSection>
);

const ClassicalWorksCitations: React.FC = () => (
  <GuideSection id="ClassicalWorks">
    <GuideHeader>Cite Classical Works</GuideHeader>
    <p>
      Citing classical works in MLA format involves various scenarios, depending
      on factors such as the type of source (print or digital), the specific
      edition or translation being cited, and the availability of specific
      information (author, title, date, etc.).
    </p>
    <p>
      Keep in mind that classical works often have unique citation requirements
      due to their historical context and the numerous editions, translations,
      or versions available.
    </p>
    <p>
      Here is the general structure for citing classical works in MLA format:
    </p>
    <CitationBlock header="Citing ancient works in MLA">
      Last Name, First Name (if available). <em>Title of Work.</em> Translated
      or edited by First Name Last Name, Publisher, Publication Date.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Homer. <em>The Iliad.</em> Translated by Robert Fagles, Penguin Books,
      1990.
    </CitationExampleBox>
    <p>
      N.B. When referencing classical works, the original publication year is
      often unknown or irrelevant; instead, provide the year of the translation
      or version you are using in parentheses, followed by a period.
    </p>
  </GuideSection>
);

const LegalDocumentsCitations: React.FC = () => (
  <GuideSection id="LegalDocuments">
    <GuideHeader>Cite Legal Documents</GuideHeader>
    <p>
      For a legal document as the main focus of your MLA citation, provide the
      title of the document in italics, the document number or code, the name of
      the authority issuing the document, the publication date, and the URL or
      DOI (if available).
    </p>
    <CitationBlock header="Citing legal materials in MLA">
      <em>Document Title.</em> Government Authority, Publication Date. Title of
      Webpage, URL or DOI (if available).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      <em>Code of Practice for Bushfire Management on Public Land.</em>{' '}
      Victorian Government Department of Sustainability and Environment, 2012.
    </CitationExampleBox>
    <p>
      <strong>For court cases</strong>, the citation structure is a bit
      different:
    </p>
    <CitationExampleBox>
      Name of Case, document number or code, jurisdiction or authority,
      publication date, URL or DOI (if available).
    </CitationExampleBox>

    <p>
      <strong>When citing the Constitution</strong>, you can use the following
      format: Title of Constitution, jurisdiction or authority, publication
      date, URL or DOI (if available).
    </p>

    <p>
      For more information and examples on how to cite different government
      documents in the MLA format, we suggest that you follow{' '}
      <a href="https://style.mla.org/documenting-legal-works/">
        these guidelines
      </a>{' '}
      from the MLA Style Center.
    </p>
  </GuideSection>
);

const OnlineLecturesCitations: React.FC = () => (
  <GuideSection id="OnlineLectures">
    <GuideHeader>Cite Online Lectures & TED Talks</GuideHeader>
    <p>
      When citing online lectures, webinars, or TED Talks, include the speaker,
      the date, the title of the talk, and the URL in your APA reference list
      entry:
    </p>
    <CitationBlock header="Citing lectures and TED Talks in MLA">
      Speaker&apos;s Last Name, First Name. &quot;Title of TED Talk.&quot;{' '}
      <em>TED or TEDx,</em> Event Name (if applicable), Release Date, URL or
      DOI.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Adichie, Chimamanda Ngozi. &quot;The Danger of a Single Story.&quot;{' '}
      <em>TED,</em> July 2009,
      www.ted.com/talks/chimamanda_ngozi_adichie_the_danger_of_a_single_story.
    </CitationExampleBox>
  </GuideSection>
);

const CourseMaterialsCitations: React.FC = () => (
  <GuideSection id="CourseMaterials">
    <GuideHeader>Cite Course Materials & Lectures in MLA</GuideHeader>
    <p>
      Course materials are considered unique academic sources and should be
      cited according to the guidelines for educational or instructional
      materials. Here is the general structure for citing course materials in
      MLA format:
    </p>
    <CitationBlock header="Citing course materials in MLA 9">
      Instructor&apos;s Last Name, First Name. &quot;Title of Material or
      Lecture.&quot; Title of Course, Institution, Date, URL or DOI (if
      available).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith, John. &quot;Introduction to Macroeconomics: Week 1 Slides.&quot;
      Economics 102, Duke University, 14 Nov. 2018, www.sakai.duke.edu/econ-101.
    </CitationExampleBox>
  </GuideSection>
);

const PersonalCommunicationsCitations: React.FC = () => (
  <GuideSection id="PersonalCommunications">
    <GuideHeader>Cite Personal Communications in MLA</GuideHeader>
    <p>
      In MLA format, personal communications, such as interviews, emails,
      letters, or personal conversations, are considered unpublished sources and
      are not typically included in the Works Cited list because they cannot be
      accessed by your readers.
    </p>
    <p>
      Instead, you should provide a parenthetical citation within the text of
      your document, giving credit to the source.
    </p>
    <p>
      When citing personal communications in your text, mention the source of
      the communication, the type of communication (e.g., email, letter,
      personal interview, etc.), and the date the communication took place.
    </p>
    <p>
      Here is the general structure for citing personal communications in MLA
      format:
    </p>
    <p>
      <IntextCitation>
        (Source&apos;s Last Name, Type of Communication, Date)
      </IntextCitation>
    </p>
    <p>
      Keep in mind that you should only cite personal communications in the body
      of your text, as they cannot be accessed by your readers and, therefore,
      should not be listed in the Works Cited page.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <ol>
      <li>
        <strong>Personal interview:</strong> According to{' '}
        <IntextCitation>John Smith</IntextCitation>, the use of renewable energy
        has increased dramatically in the last decade{' '}
        <IntextCitation>(personal interview, 7 May 2023)</IntextCitation>.
      </li>
      <li>
        <strong>Email:</strong> <IntextCitation>Jane Doe</IntextCitation>{' '}
        mentioned in <IntextCitation>an email</IntextCitation> that her research
        findings support the use of electric vehicles to reduce greenhouse gas
        emissions <IntextCitation>(12 April 2023)</IntextCitation>.
      </li>
      <li>
        <strong>Letter:</strong> She recently expressed her concerns about the
        environmental impact of deforestation{' '}
        <IntextCitation>(Sarah Johnson, letter, 10 June 2022)</IntextCitation>.
      </li>
    </ol>
    <p>
      Always ensure that you have the permission of the person you are citing to
      include their personal communication in your work, as this information is
      not publicly accessible and may involve privacy concerns.
    </p>
  </GuideSection>
);

const FinalNotes: React.FC = () => (
  <GuideSection>
    <GuideHeader>MLA Style: Final Notes</GuideHeader>
    <p>
      As you can see, the MLA citation format provides a consistent and clear
      method for citing various types of sources in your research. By mastering
      the MLA citation style, you will not only make your work more professional
      but also help your readers easily locate the sources you have used.
    </p>
    <p>
      We hope this comprehensive guide on MLA citation style has provided you
      with a solid foundation for citing sources in your research. Remember that
      <Link href="/">Bibcitation</Link> is here to help you with your academic
      needs, offering accurate and easy-to-use tools.
    </p>
  </GuideSection>
);

export default MlaGuide;
